import { DependencyList, useEffect } from 'react';

import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetCumulativeOrderList } from 'src/actions';
import { GetOrderListQueryParamsDto } from 'src/dtos';
import { Order } from 'src/models';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';

interface UseCumulativeOrdersReturnType {
  cumulativeOrderList: Order[];
  isCumulativeOrderListLoading: boolean;
  hasCumulativeOrderListError?: boolean;
  cumulativeOrderListError?: string;
  cumulativeOrderListTotal: number;
}

export interface UseCumulativeOrdersOptions extends CustomHookBaseOptions {
  refetchOnChange?: boolean;
  queryParamsDto?: Omit<GetOrderListQueryParamsDto, 'pageIndex' | 'pageSize'>;
  dependency?: DependencyList;
}

const EMPTY_ORDERS: Order[] = [];

export const useCumulativeOrders = (
  { skip, refetchOnChange, queryParamsDto, dependency = [] }: UseCumulativeOrdersOptions = { skip: false },
): UseCumulativeOrdersReturnType => {
  const dispatch = useDispatch();
  const orderList: Order[] | undefined = useSelector((state: any) => state.orders.cumulativeList?.data?.data);
  const isOrderListLoading = useSelector((state: any) => Boolean(state.orders?.cumulativeList?.isLoading));
  const hasOrderListFailed: boolean = useSelector((state: any) => state.orders.cumulativeList?.__failed);
  const orderListError: string = useSelector((state: any) => state.orders.cumulativeList?.__message);
  const payload = useSelector((state: any) => state.orders.cumulativeList?.payload);
  const orderListTotal: number = useSelector((state: any) => state.orders.cumulativeList?.data?.total ?? 0);

  const { account } = useAccountSelector();

  const fetchList = () => {
    if (!account?.accountId) {
      return;
    }

    dispatch(
      doGetCumulativeOrderList({
        params: { accountId: account.accountId },
        queryParams: {
          ...queryParamsDto,
          pageIndex: 0,
          pageSize: 50,
        },
      }),
    );
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!orderList && !isOrderListLoading && !hasOrderListFailed) {
      fetchList();

      return;
    }

    if (
      refetchOnChange ||
      !isEqual(payload, {
        params: { accountId: account?.accountId },
        queryParams: { ...queryParamsDto, pageIndex: 0, pageSize: 50 },
      })
    ) {
      fetchList();
    }
  }, dependency);

  return {
    cumulativeOrderList: Array.isArray(orderList) ? orderList : EMPTY_ORDERS,
    isCumulativeOrderListLoading: isOrderListLoading,
    hasCumulativeOrderListError: hasOrderListFailed,
    cumulativeOrderListError: orderListError,
    cumulativeOrderListTotal: orderListTotal,
  };
};
